import { useEffect, useState } from "react";

/**
 * Viewport state published by the host. Mirrors
 * apps/ispm/src/contexts/ViewportContext.tsx — keep in sync.
 */
export interface HostViewport {
  isMenuPinned: boolean;
  isMenuExpanded: boolean;
  sidenavWidth: number;
  headerHeight: number;
  viewportWidth: number;
  viewportHeight: number;
}

const VIEWPORT_EVENT = "saviynt:viewport:change";
const VIEWPORT_GLOBAL_KEY = "__SAVIYNT_VIEWPORT__";

declare global {
  interface Window {
    __SAVIYNT_VIEWPORT__?: HostViewport;
  }
}

function getWindowSize() {
  if (typeof window === "undefined") return { width: 0, height: 0 };
  return { width: window.innerWidth, height: window.innerHeight };
}

const SIDENAV_EXPANDED_WIDTH = 240;
const SIDENAV_COLLAPSED_WIDTH = 60;

function defaultsFor(isMenuPinned: boolean): HostViewport {
  const { width, height } = getWindowSize();
  const sidenavWidth = isMenuPinned
    ? SIDENAV_EXPANDED_WIDTH
    : SIDENAV_COLLAPSED_WIDTH;
  return {
    isMenuPinned,
    isMenuExpanded: isMenuPinned,
    sidenavWidth,
    headerHeight: 0,
    viewportWidth: Math.max(0, width - sidenavWidth),
    viewportHeight: height,
  };
}

function readFromLegacyStore(): boolean | null {
  if (typeof window === "undefined" || !window.store) return null;
  try {
    const state = window.store.getState() as {
      user?: { isMenuPinned?: boolean };
    };
    return state?.user?.isMenuPinned ?? null;
  } catch {
    return null;
  }
}

function resolveSnapshot(): HostViewport {
  // Tier 1 — ispm host viewport global.
  if (typeof window !== "undefined" && window[VIEWPORT_GLOBAL_KEY]) {
    return window[VIEWPORT_GLOBAL_KEY] as HostViewport;
  }
  // Tier 2 — legacy ui-track Redux store.
  const legacyPinned = readFromLegacyStore();
  if (legacyPinned !== null) return defaultsFor(legacyPinned);
  // Tier 3 — safe defaults.
  return defaultsFor(false);
}

/**
 * Subscribe to the host-published viewport state. Works under:
 *   - ispm host (Module Federation) via window.__SAVIYNT_VIEWPORT__ +
 *     "saviynt:viewport:change" CustomEvent.
 *   - ui-track legacy host via window.store Redux state.
 *   - Standalone dev — returns sensible defaults.
 */
export function useHostViewport(): HostViewport {
  const [snapshot, setSnapshot] = useState<HostViewport>(() =>
    resolveSnapshot(),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const sync = () => setSnapshot(resolveSnapshot());

    // ispm host — subscribe to custom viewport events.
    window.addEventListener(VIEWPORT_EVENT, sync);

    // Legacy host — subscribe to Redux updates if a store is present.
    let unsubscribeLegacy: (() => void) | undefined;
    let pollId: ReturnType<typeof setInterval> | undefined;

    const trySubscribeLegacy = () => {
      if (!window.store) return false;
      try {
        sync();
        unsubscribeLegacy = window.store.subscribe(sync);
        return true;
      } catch {
        return false;
      }
    };

    if (!window[VIEWPORT_GLOBAL_KEY] && !trySubscribeLegacy()) {
      pollId = setInterval(() => {
        if (window[VIEWPORT_GLOBAL_KEY]) {
          sync();
          if (pollId !== undefined) clearInterval(pollId);
        } else if (trySubscribeLegacy() && pollId !== undefined) {
          clearInterval(pollId);
        }
      }, 50);
    }

    return () => {
      window.removeEventListener(VIEWPORT_EVENT, sync);
      if (pollId !== undefined) clearInterval(pollId);
      unsubscribeLegacy?.();
    };
  }, []);

  return snapshot;
}
